import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Api } from "services/api/api.service";
import type {
  EmploymentTemplateListItem,
  EmploymentTemplateSalary,
} from "services/api/company/staff.service";
import { filterTemplateSalariesByAge } from "../../../shared/staffEmploymentTemplate.logic";
import { getAgeFromPersonalIdentityNumber } from "../../../shared/staffPersonalIdentityNumber.logic";
import type { EmploymentDetailsForm } from "../../profilePanel.contract";
import { applyEmploymentTemplateToEmploymentForm } from "./profilePanel.employmentTemplate.logic";

interface UseEmploymentTemplateSelectionParams {
  companyId: number | null;
  /** Whether the new-employment (create) flow is currently active. */
  isActive: boolean;
  personalIdentityNumber: string | null;
  setForm: Dispatch<SetStateAction<EmploymentDetailsForm>>;
}

interface UseEmploymentTemplateSelectionResult {
  employmentTemplates: EmploymentTemplateListItem[];
  isLoadingEmploymentTemplates: boolean;
  selectedEmploymentTemplateId: string;
  applyEmploymentTemplate: (templateId: string) => void;
  templateSalaries: EmploymentTemplateSalary[];
  selectedTemplateSalaryId: string;
  applyEmploymentTemplateSalary: (salaryId: string) => void;
}

/**
 * Encapsulates employment-template selection for the profile panel's
 * new-employment flow: fetching templates, prefilling the form on selection,
 * and age-filtering the template salaries. Both dropdown selections only change
 * on an explicit pick; editing form fields never resets them.
 */
export const useEmploymentTemplateSelection = (
  params: UseEmploymentTemplateSelectionParams,
): UseEmploymentTemplateSelectionResult => {
  const { companyId, isActive, personalIdentityNumber, setForm } = params;

  const [employmentTemplates, setEmploymentTemplates] = useState<
    EmploymentTemplateListItem[]
  >([]);
  const [isLoadingEmploymentTemplates, setIsLoadingEmploymentTemplates] =
    useState(false);
  const [selectedEmploymentTemplateId, setSelectedEmploymentTemplateId] =
    useState("");
  const [templateSalaries, setTemplateSalaries] = useState<
    EmploymentTemplateSalary[]
  >([]);
  const [selectedTemplateSalaryId, setSelectedTemplateSalaryId] = useState("");
  const templatesRequestIdRef = useRef(0);
  const templateDetailRequestIdRef = useRef(0);

  const fetchEmploymentTemplates = useCallback((): void => {
    if (companyId == null) return;
    const requestId = templatesRequestIdRef.current + 1;
    templatesRequestIdRef.current = requestId;
    setIsLoadingEmploymentTemplates(true);

    void Api()
      .company.staff.getEmploymentTemplates(companyId)
      .then((response) => {
        if (templatesRequestIdRef.current !== requestId) return;
        setEmploymentTemplates(response.data ?? []);
      })
      .catch(() => {
        console.error("Failed to fetch employment templates for profile panel");
      })
      .finally(() => {
        if (templatesRequestIdRef.current !== requestId) return;
        setIsLoadingEmploymentTemplates(false);
      });
  }, [companyId]);

  // Fetch templates when the create flow opens; clear everything when it closes.
  useEffect(() => {
    templateDetailRequestIdRef.current += 1;
    setSelectedEmploymentTemplateId("");
    setSelectedTemplateSalaryId("");
    setTemplateSalaries([]);
    if (!isActive) {
      setEmploymentTemplates([]);
      return;
    }
    fetchEmploymentTemplates();
  }, [isActive, fetchEmploymentTemplates]);

  const applyEmploymentTemplate = useCallback(
    (templateId: string): void => {
      setSelectedEmploymentTemplateId(templateId);
      setSelectedTemplateSalaryId("");
      setTemplateSalaries([]);
      if (companyId == null || templateId.length === 0) return;

      const requestId = templateDetailRequestIdRef.current + 1;
      templateDetailRequestIdRef.current = requestId;

      void Api()
        .company.staff.getEmploymentTemplate(companyId, Number(templateId))
        .then((response) => {
          if (templateDetailRequestIdRef.current !== requestId) return;
          const template = response.data;
          if (template == null) return;
          setForm((previousForm) =>
            applyEmploymentTemplateToEmploymentForm(previousForm, template),
          );
          setTemplateSalaries(template.salaries ?? []);
        })
        .catch(() => {
          console.error(
            "Failed to fetch employment template for profile panel",
          );
        });
    },
    [companyId, setForm],
  );

  const applyEmploymentTemplateSalary = useCallback(
    (salaryId: string): void => {
      setSelectedTemplateSalaryId(salaryId);
      const selectedSalary = templateSalaries.find(
        (salary) => String(salary.id) === salaryId,
      );
      if (selectedSalary == null) return;
      setForm((previousForm) => ({
        ...previousForm,
        salary: String(selectedSalary.value),
      }));
    },
    [setForm, templateSalaries],
  );

  const workerAge = useMemo(() => {
    return getAgeFromPersonalIdentityNumber(personalIdentityNumber);
  }, [personalIdentityNumber]);

  const visibleTemplateSalaries = useMemo(() => {
    return filterTemplateSalariesByAge(templateSalaries, workerAge);
  }, [templateSalaries, workerAge]);

  return {
    employmentTemplates,
    isLoadingEmploymentTemplates,
    selectedEmploymentTemplateId,
    applyEmploymentTemplate,
    templateSalaries: visibleTemplateSalaries,
    selectedTemplateSalaryId,
    applyEmploymentTemplateSalary,
  };
};
